/**
 * Thin fetch wrapper that automatically attaches the WorkOS Bearer token from
 * localStorage to every request.  On 401, silently refreshes the token and
 * retries the original request once before forcing a logout.
 */
import { getAccessToken, getRefreshToken, storeSession, clearSession } from './auth'
import type { AuthUser } from './auth'

// API base URL is injected at build time via Vite. In dev (no env var set),
// fall back to the local FastAPI dev server.
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000'

/** Mutex: only one refresh request can be in-flight at a time. */
let refreshPromise: Promise<boolean> | null = null

function forceLogout(): void {
  clearSession()
  window.location.href = '/'
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return false

    const data = await res.json() as {
      access_token: string
      refresh_token: string
      user: AuthUser
    }
    storeSession(data.access_token, data.user, data.refresh_token)
    return true
  } catch {
    return false
  }
}

/** Coalesce concurrent refresh attempts into a single in-flight request. */
function doRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

function buildRequest(path: string, init: RequestInit): Request {
  const token = getAccessToken()
  const headers = new Headers(init.headers)

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  // Let the browser set Content-Type automatically for FormData (multipart boundary)
  if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  return new Request(`${API_BASE}${path}`, { ...init, headers })
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const request = buildRequest(path, init)
  const response = await fetch(request)

  if (response.status !== 401) return response

  // 401 — attempt silent refresh then retry once
  const refreshed = await doRefresh()
  if (!refreshed) {
    forceLogout()
    return response
  }

  // Retry with new token
  const retryRequest = buildRequest(path, init)
  const retryResponse = await fetch(retryRequest)

  if (retryResponse.status === 401) {
    forceLogout()
  }

  return retryResponse
}
